import { useCallback, useEffect, useRef } from 'react';
import type { ShuttlePacingEvent } from '../shuttle-walk/shuttleWalkDomain';

type UseScheduledBeepsOptions = {
  events: ShuttlePacingEvent[];
  muted?: boolean;
  volume?: number;
};

const TRIPLE_BEEP_OFFSETS = [0, 0.16, 0.32];

export function useScheduledBeeps({
  events,
  muted = false,
  volume = 0.08,
}: UseScheduledBeepsOptions) {
  const audioContextRef = useRef<AudioContext | null>(null);
  const scheduledTimeoutsRef = useRef<number[]>([]);

  const clearScheduledTimeouts = useCallback(() => {
    scheduledTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    scheduledTimeoutsRef.current = [];
  }, []);

  const playOscillator = useCallback((when: number, frequency: number, gainValue: number) => {
    const context = audioContextRef.current;
    if (!context) return;

    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(frequency, when);

    gainNode.gain.setValueAtTime(0.0001, when);
    gainNode.gain.exponentialRampToValueAtTime(gainValue, when + 0.004);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, when + 0.07);

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.start(when);
    oscillator.stop(when + 0.075);
  }, []);

  const playSingle = useCallback(() => {
    if (muted) return;
    const context = audioContextRef.current;
    if (!context || context.state !== 'running') return;
    playOscillator(context.currentTime, 920, volume);
  }, [muted, playOscillator, volume]);

  const playTriple = useCallback(() => {
    if (muted) return;
    const context = audioContextRef.current;
    if (!context || context.state !== 'running') return;
    TRIPLE_BEEP_OFFSETS.forEach((offset) => {
      playOscillator(context.currentTime + offset, 1280, Math.min(0.14, volume * 1.35));
    });
  }, [muted, playOscillator, volume]);

  const ensureContext = useCallback(async () => {
    if (typeof window === 'undefined') return;

    const AudioContextCtor =
      window.AudioContext ??
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!AudioContextCtor) return;

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextCtor();
    }

    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }

    const context = audioContextRef.current;
    if (!context || context.state !== 'running') return;

    const unlockOscillator = context.createOscillator();
    const unlockGain = context.createGain();
    unlockGain.gain.setValueAtTime(0.0001, context.currentTime);
    unlockOscillator.connect(unlockGain);
    unlockGain.connect(context.destination);
    unlockOscillator.start(context.currentTime);
    unlockOscillator.stop(context.currentTime + 0.01);
  }, []);

  const startFrom = useCallback(async (elapsedSeconds = 0) => {
    clearScheduledTimeouts();
    await ensureContext();
    if (muted) return;

    const remainingEvents = events.filter((event) => event.atSeconds >= elapsedSeconds);
    scheduledTimeoutsRef.current = remainingEvents.map((event) =>
      window.setTimeout(
        () => {
          if (event.kind === 'triple') {
            playTriple();
            return;
          }
          playSingle();
        },
        Math.max(0, Math.round((event.atSeconds - elapsedSeconds) * 1000)),
      ),
    );
  }, [clearScheduledTimeouts, ensureContext, events, muted, playSingle, playTriple]);

  useEffect(() => {
    return () => {
      clearScheduledTimeouts();
      const context = audioContextRef.current;
      audioContextRef.current = null;
      if (context && context.state !== 'closed') {
        void context.close().catch(() => undefined);
      }
    };
  }, [clearScheduledTimeouts]);

  return {
    clearScheduledTimeouts,
    ensureContext,
    playSingle,
    playTriple,
    startFrom,
  };
}
